import React, { useEffect, useState, memo } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BottomNav } from '../components/BottomNav';
import { ChevronRight, Gem, Star, X, Shield, Link, Camera, Lock, Bell, Moon, Sun, Globe, ExternalLink, Check, Sparkles } from 'lucide-react';
interface SettingsPageProps {
  onNavigate: (page: string) => void;
}
interface SettingsItemProps {
  emoji: string;
  label: string;
  value?: string;
  onClick?: () => void;
  isDestructive?: boolean;
}
function SettingsItem({
  emoji,
  label,
  value,
  onClick,
  isDestructive
}: SettingsItemProps) {
  return <button onClick={onClick} className="w-full flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl hover:border-green-500 transition-all duration-150 group" data-id="element-1297">
      <div className="flex items-center gap-3" data-id="element-1298">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${isDestructive ? 'bg-red-50' : 'bg-gray-50 group-hover:bg-green-50'} transition-colors`} data-id="element-1299">
          {emoji}
        </div>
        <span className={`text-sm font-medium ${isDestructive ? 'text-red-600' : 'text-gray-900'}`} data-id="element-1300">
          {label}
        </span>
      </div>
      <div className="flex items-center gap-2" data-id="element-1301">
        {value && <span className="text-xs text-gray-500" data-id="element-1302">{value}</span>}
        <ChevronRight className="h-4 w-4 text-gray-400" data-id="element-1303" />
      </div>
    </button>;
}
export function SettingsPage({
  onNavigate
}: SettingsPageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showImpactDetailModal, setShowImpactDetailModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showAppearanceModal, setShowAppearanceModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [closingModal, setClosingModal] = useState<string | null>(null);
  // Settings state
  const [darkModeUnlocked, setDarkModeUnlocked] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [language, setLanguage] = useState('it');
  const [shareData, setShareData] = useState(true);
  const [notifications, setNotifications] = useState({
    actions: true,
    rewards: true,
    community: false,
    marketing: false
  });
  // Mock wallet stars (matches AppContext initial value)
  const userStars = 400;
  const darkModeCost = 50;
  const closeModal = (modalName: string, setter: (v: boolean) => void) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setter(false);
      setClosingModal(null);
    }, 300);
  };
  const handleUnlockDarkMode = () => {
    setDarkModeUnlocked(true);
    setTheme('dark');
  };
  useEffect(() => {
    const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
    const handleScroll = () => {
      if (scrollArea) {
        setIsScrolled(scrollArea.scrollTop > 20);
      }
    };
    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      return () => scrollArea.removeEventListener('scroll', handleScroll);
    }
  }, []);
  // Helper to get animation class per modal
  const animClass = (name: string) => closingModal === name ? 'animate-slide-down' : 'animate-slide-up';
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-1304">
      {/* Fixed Header */}
      <header className={`fixed top-0 left-0 right-0 px-6 py-4 flex justify-between items-center z-30 transition-all duration-300 ${isScrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`} data-id="element-1305">
        <div className="flex items-center" data-id="element-1306">
          <h1 className={`text-xl font-normal transition-colors duration-300 ${isScrolled ? 'text-gray-900' : 'text-white'}`} data-id="element-1307">
            Impostazioni
          </h1>
        </div>
        <div className="flex items-center gap-3" data-id="element-1308">
          <div className={`flex items-center gap-1.5 transition-colors duration-300 ${isScrolled ? 'text-green-600' : 'text-white'}`} data-id="element-1309">
            <Gem className="h-5 w-5" strokeWidth={2} data-id="element-1310" />
            <span className="text-sm font-normal" data-id="element-1311">150</span>
          </div>
          <div className={`flex items-center gap-1.5 transition-colors duration-300 ${isScrolled ? 'text-gray-600' : 'text-white'}`} data-id="element-1312">
            <Star className="h-5 w-5" strokeWidth={2} data-id="element-1313" />
            <span className="text-sm font-normal" data-id="element-1314">{userStars}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <ScrollArea className="flex-1" data-id="element-1315">
        {/* Hero Background */}
        <div className="bg-green-600 w-full relative pt-24 pb-8" data-id="element-1316">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
          backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/wsCbUWp4TF3ni7DwEfb9qP/hand-drawn-turtle-shell-pattern_23-2150598661.jpg)'
        }} data-id="element-1317" />
          <div className="relative z-10 px-6 max-w-md mx-auto" data-id="element-1318">
            <div className="flex items-center gap-4 mb-6" data-id="element-1319">
              <div className="h-16 w-16 rounded-full border-2 border-white/30 overflow-hidden bg-white" data-id="element-1320">
                <img src="https://cdn.magicpatterns.com/uploads/wAuXNp8Njv5DkmF4gEUeC7/jonathan-cooper-ViyA5myhBVw-unsplash.jpg" alt="Profile" className="h-full w-full object-cover" data-id="element-1321" />
              </div>
              <div data-id="element-1322">
                <h2 className="text-xl font-medium text-white" data-id="element-1323">Alex Rossi</h2>
                <p className="text-sm text-green-100" data-id="element-1324">Membro dal 2023</p>
              </div>
            </div>

            {/* Impact Index */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20" data-id="element-1325">
              <div className="flex items-center justify-between mb-2" data-id="element-1326">
                <div data-id="element-1327">
                  <span className="text-sm text-green-50" data-id="element-1328">Impatto Totale</span>
                  <p className="text-xs text-green-100 mt-1" data-id="element-1329">
                    12 azioni completate
                  </p>
                </div>
                <span className="text-3xl font-medium text-white" data-id="element-1330">
                  85<span className="text-lg" data-id="element-1331">%</span>
                </span>
              </div>
              <button onClick={() => setShowImpactDetailModal(true)} className="flex items-center gap-1 text-xs text-white hover:text-green-100 transition-colors" data-id="element-1332">
                Vedi dettaglio tracciamento
                <ChevronRight className="h-3 w-3" data-id="element-1333" />
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-8 max-w-md mx-auto relative z-20 pb-28" data-id="element-1334">
          {/* Account Settings */}
          <section className="space-y-3" data-id="element-1335">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-1" data-id="element-1336">
              Account
            </h3>
            <div className="space-y-2" data-id="element-1337">
              <SettingsItem emoji="👤" label="Profilo" value="Alex Rossi" onClick={() => setShowProfileModal(true)} data-id="element-1338" />
              <SettingsItem emoji="🔒" label="Password e sicurezza" onClick={() => setShowPasswordModal(true)} data-id="element-1339" />
              <SettingsItem emoji="🔔" label="Notifiche" value="Attive" onClick={() => setShowNotificationsModal(true)} data-id="element-1340" />
              <SettingsItem emoji="🛡️" label="Privacy" onClick={() => setShowPrivacyModal(true)} data-id="element-1341" />
            </div>
          </section>

          {/* App Settings */}
          <section className="space-y-3" data-id="element-1342">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-1" data-id="element-1343">
              Preferenze
            </h3>
            <div className="space-y-2" data-id="element-1344">
              <SettingsItem emoji="🌙" label="Aspetto" value={theme === 'light' ? 'Chiaro' : 'Scuro'} onClick={() => setShowAppearanceModal(true)} data-id="element-1345" />
              <SettingsItem emoji="🌍" label="Lingua e regione" value={language === 'it' ? 'Italiano' : 'English'} onClick={() => setShowLanguageModal(true)} data-id="element-1346" />
            </div>
          </section>

          {/* Community & Resources */}
          <section className="space-y-3" data-id="element-1347">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-1" data-id="element-1348">
              Risorse
            </h3>
            <div className="space-y-2" data-id="element-1349">
              <SettingsItem emoji="👥" label="Community" onClick={() => window.open('https://greenback-social-learning.dmoodlabs.com/community', '_blank')} data-id="element-1350" />
              <SettingsItem emoji="🎓" label="Piattaforma Formazione" onClick={() => window.open('https://greenback-social-learning.dmoodlabs.com/', '_blank')} data-id="element-1351" />
            </div>
          </section>

          {/* Partner Program */}
          <section data-id="element-1352">
            <div className="bg-green-600 rounded-2xl p-6 text-center" data-id="element-1353">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 shadow-sm text-2xl" data-id="element-1354">
                💼
              </div>
              <h3 className="text-lg font-medium text-white mb-2" data-id="element-1355">
                Diventa Partner
              </h3>
              <p className="text-sm text-white/90 mb-6 leading-relaxed" data-id="element-1356">
                Hai un'azienda sostenibile? Unisciti a noi per offrire vantaggi
                esclusivi alla community.
              </p>
              <button className="w-full py-3 px-4 rounded-xl text-sm font-medium text-green-600 bg-white hover:bg-gray-50 transition-all shadow-sm" data-id="element-1357">
                Richiedi informazioni
              </button>
            </div>
          </section>

          {/* Logout */}
          <button onClick={() => onNavigate('login')} className="w-full flex items-center justify-center gap-2 p-4 text-red-600 hover:bg-red-50 rounded-xl transition-colors" data-id="element-1358">
            <span className="text-xl" data-id="element-1359">🚪</span>
            <span className="text-sm font-medium" data-id="element-1360">Esci dall'account</span>
          </button>

          <div className="text-center pb-4" data-id="element-1361">
            <p className="text-xs text-gray-400" data-id="element-1362">Versione 1.0.2 (Build 452)</p>
          </div>
        </div>
      </ScrollArea>

      <BottomNav activePage="settings" onNavigate={onNavigate} data-id="element-1363" />

      {/* ── Profile Modal ── */}
      {showProfileModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => closeModal('profile', setShowProfileModal)} data-id="element-1364" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${animClass('profile')}`} data-id="element-1365">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1366">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1367" />
              <div className="flex items-center justify-between" data-id="element-1368">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1369">
                  Modifica Profilo
                </h3>
                <button onClick={() => closeModal('profile', setShowProfileModal)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1370">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1371" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1372">
              <div className="space-y-6" data-id="element-1373">
                <div className="flex flex-col items-center" data-id="element-1374">
                  <div className="relative" data-id="element-1375">
                    <div className="h-24 w-24 rounded-full overflow-hidden bg-gray-100" data-id="element-1376">
                      <img src="https://cdn.magicpatterns.com/uploads/wAuXNp8Njv5DkmF4gEUeC7/jonathan-cooper-ViyA5myhBVw-unsplash.jpg" alt="Profile" className="h-full w-full object-cover" data-id="element-1377" />
                    </div>
                    <button className="absolute bottom-0 right-0 bg-green-600 rounded-full p-2 text-white hover:bg-green-700 transition-colors" data-id="element-1378">
                      <Camera className="h-4 w-4" data-id="element-1379" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2" data-id="element-1380">
                    Clicca per cambiare foto
                  </p>
                </div>
                <div data-id="element-1381">
                  <label className="block text-sm font-normal text-gray-900 mb-2" data-id="element-1382">
                    Nome
                  </label>
                  <input type="text" defaultValue="Alex" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 transition-colors" data-id="element-1383" />
                </div>
                <div data-id="element-1384">
                  <label className="block text-sm font-normal text-gray-900 mb-2" data-id="element-1385">
                    Cognome
                  </label>
                  <input type="text" defaultValue="Rossi" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 transition-colors" data-id="element-1386" />
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white" data-id="element-1387">
              <button className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-1388">
                Salva modifiche
              </button>
            </div>
          </div>
        </>}

      {/* ── Password Modal ── */}
      {showPasswordModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => closeModal('password', setShowPasswordModal)} data-id="element-1389" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${animClass('password')}`} data-id="element-1390">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1391">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1392" />
              <div className="flex items-center justify-between" data-id="element-1393">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1394">
                  Password e Sicurezza
                </h3>
                <button onClick={() => closeModal('password', setShowPasswordModal)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1395">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1396" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1397">
              <div className="space-y-6" data-id="element-1398">
                <div data-id="element-1399">
                  <label className="block text-sm font-normal text-gray-900 mb-2" data-id="element-1400">
                    Email
                  </label>
                  <input type="email" defaultValue="alex.rossi@email.com" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 transition-colors" data-id="element-1401" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1402">
                    Riceverai un'email di conferma
                  </p>
                </div>
                <div data-id="element-1403">
                  <label className="block text-sm font-normal text-gray-900 mb-2" data-id="element-1404">
                    Nuova Password
                  </label>
                  <input type="password" placeholder="••••••••" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 transition-colors" data-id="element-1405" />
                </div>
                <div data-id="element-1406">
                  <label className="block text-sm font-normal text-gray-900 mb-2" data-id="element-1407">
                    Conferma Password
                  </label>
                  <input type="password" placeholder="••••••••" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 transition-colors" data-id="element-1408" />
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white" data-id="element-1409">
              <button className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-1410">
                Aggiorna credenziali
              </button>
            </div>
          </div>
        </>}

      {/* ── Notifications Modal ── */}
      {showNotificationsModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => closeModal('notifications', setShowNotificationsModal)} data-id="element-1411" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${animClass('notifications')}`} data-id="element-1412">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1413">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1414" />
              <div className="flex items-center justify-between" data-id="element-1415">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1416">Notifiche</h3>
                <button onClick={() => closeModal('notifications', setShowNotificationsModal)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1417">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1418" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1419">
              <div className="space-y-4" data-id="element-1420">
                {[{
              key: 'actions',
              label: 'Promemoria azioni',
              desc: 'Ricevi notifiche per le azioni pianificate'
            }, {
              key: 'rewards',
              label: 'Premi e ricompense',
              desc: 'Aggiornamenti sui tuoi premi'
            }, {
              key: 'community',
              label: 'Attività community',
              desc: 'Eventi e novità dalla community'
            }, {
              key: 'marketing',
              label: 'Offerte e promozioni',
              desc: 'Offerte esclusive dai partner'
            }].map(item => <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl" data-id="element-1421">
                    <div className="flex-1" data-id="element-1422">
                      <p className="text-sm font-normal text-gray-900" data-id="element-1423">
                        {item.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-1424">
                        {item.desc}
                      </p>
                    </div>
                    <button onClick={() => setNotifications(prev => ({
                ...prev,
                [item.key]: !prev[item.key as keyof typeof notifications]
              }))} className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${notifications[item.key as keyof typeof notifications] ? 'bg-green-600' : 'bg-gray-300'}`} data-id="element-1425">
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${notifications[item.key as keyof typeof notifications] ? 'translate-x-6' : 'translate-x-0.5'}`} data-id="element-1426" />
                    </button>
                  </div>)}
              </div>
            </div>
            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white" data-id="element-1427">
              <button onClick={() => closeModal('notifications', setShowNotificationsModal)} className="w-full py-3 px-4 rounded-full text-sm font-normal text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-all" data-id="element-1428">
                Chiudi
              </button>
            </div>
          </div>
        </>}

      {/* ── Privacy Modal ── */}
      {showPrivacyModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => closeModal('privacy', setShowPrivacyModal)} data-id="element-1429" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${animClass('privacy')}`} data-id="element-1430">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1431">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1432" />
              <div className="flex items-center justify-between" data-id="element-1433">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1434">Privacy</h3>
                <button onClick={() => closeModal('privacy', setShowPrivacyModal)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1435">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1436" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1437">
              <div className="space-y-6" data-id="element-1438">
                <div className="bg-gray-50 rounded-xl p-4" data-id="element-1439">
                  <h4 className="text-sm font-normal text-gray-900 mb-2" data-id="element-1440">
                    Informativa Privacy
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1441">
                    I tuoi dati sono protetti e utilizzati solo per migliorare
                    la tua esperienza su Greenback. Non condividiamo
                    informazioni personali senza il tuo consenso.
                  </p>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl" data-id="element-1442">
                  <div className="flex-1" data-id="element-1443">
                    <p className="text-sm font-normal text-gray-900" data-id="element-1444">
                      Condividi dati con partner
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-1445">
                      Permetti ai partner di personalizzare le offerte
                    </p>
                  </div>
                  <button onClick={() => setShareData(!shareData)} className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${shareData ? 'bg-green-600' : 'bg-gray-300'}`} data-id="element-1446">
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${shareData ? 'translate-x-6' : 'translate-x-0.5'}`} data-id="element-1447" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ── Appearance Modal ── */}
      {showAppearanceModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => closeModal('appearance', setShowAppearanceModal)} data-id="element-1448" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${animClass('appearance')}`} data-id="element-1449">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1450">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1451" />
              <div className="flex items-center justify-between" data-id="element-1452">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1453">Aspetto</h3>
                <button onClick={() => closeModal('appearance', setShowAppearanceModal)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1454">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1455" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1456">
              <div className="space-y-4" data-id="element-1457">
                {/* Light theme option */}
                <button onClick={() => setTheme('light')} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${theme === 'light' ? 'border-green-600 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1458">
                  <div className="flex items-center gap-3" data-id="element-1459">
                    <Sun className="h-5 w-5 text-gray-600" data-id="element-1460" />
                    <span className="text-sm font-normal text-gray-900" data-id="element-1461">
                      Tema Chiaro
                    </span>
                  </div>
                  {theme === 'light' && <Check className="h-5 w-5 text-green-600" data-id="element-1462" />}
                </button>

                {/* Dark theme option — paywall if not unlocked */}
                {darkModeUnlocked ? <button onClick={() => setTheme('dark')} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${theme === 'dark' ? 'border-green-600 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1463">
                    <div className="flex items-center gap-3" data-id="element-1464">
                      <Moon className="h-5 w-5 text-gray-600" data-id="element-1465" />
                      <span className="text-sm font-normal text-gray-900" data-id="element-1466">
                        Tema Scuro
                      </span>
                    </div>
                    {theme === 'dark' && <Check className="h-5 w-5 text-green-600" data-id="element-1467" />}
                  </button> : <div className="rounded-xl border-2 border-gray-200 bg-gray-50 overflow-hidden" data-id="element-1468">
                    <div className="p-4" data-id="element-1469">
                      <div className="flex items-start gap-3 mb-4" data-id="element-1470">
                        <Moon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-1471" />
                        <div className="flex-1" data-id="element-1472">
                          <div className="flex items-center gap-2 mb-1" data-id="element-1473">
                            <span className="text-sm font-normal text-gray-700" data-id="element-1474">
                              Tema Scuro
                            </span>
                            <span className="text-[10px] bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full uppercase tracking-wide" data-id="element-1475">
                              Premium
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed" data-id="element-1476">
                            Il tema scuro è un contenuto esclusivo per i membri
                            più attivi. Sbloccalo usando le tue Stelle
                            guadagnate completando azioni sostenibili.
                          </p>
                        </div>
                      </div>

                      {/* Balance vs cost */}
                      <div className="bg-white rounded-lg p-3 border border-gray-200 mb-3" data-id="element-1477">
                        <div className="flex items-center justify-between mb-2" data-id="element-1478">
                          <span className="text-xs text-gray-500" data-id="element-1479">
                            Il tuo saldo
                          </span>
                          <div className="flex items-center gap-1 text-xs font-medium text-gray-700" data-id="element-1480">
                            <Star className="h-3.5 w-3.5 text-gray-500" strokeWidth={2} data-id="element-1481" />
                            <span data-id="element-1482">{userStars} Stelle</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mb-2" data-id="element-1483">
                          <span className="text-xs text-gray-500" data-id="element-1484">
                            Costo sblocco
                          </span>
                          <div className="flex items-center gap-1 text-xs font-medium text-gray-700" data-id="element-1485">
                            <Star className="h-3.5 w-3.5 text-gray-500" strokeWidth={2} data-id="element-1486" />
                            <span data-id="element-1487">{darkModeCost} Stelle</span>
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1" data-id="element-1488">
                          <div className="h-full rounded-full bg-green-500 transition-all" style={{
                      width: `${Math.min(darkModeCost / userStars * 100, 100)}%`
                    }} data-id="element-1489" />
                        </div>
                        <p className="text-[10px] text-green-600" data-id="element-1490">
                          ✓ Hai abbastanza Stelle per sbloccare questa funzione
                        </p>
                      </div>

                      <button onClick={handleUnlockDarkMode} className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-full text-sm font-normal text-white bg-gray-700 hover:bg-gray-800 transition-colors" data-id="element-1491">
                        <Sparkles className="h-4 w-4" data-id="element-1492" />
                        Sblocca con {darkModeCost} Stelle
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </>}

      {/* ── Language Modal ── */}
      {showLanguageModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => closeModal('language', setShowLanguageModal)} data-id="element-1493" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${animClass('language')}`} data-id="element-1494">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1495">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1496" />
              <div className="flex items-center justify-between" data-id="element-1497">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1498">
                  Lingua e Regione
                </h3>
                <button onClick={() => closeModal('language', setShowLanguageModal)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1499">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1500" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1501">
              <div className="space-y-3" data-id="element-1502">
                {[{
              code: 'it',
              label: 'Italiano',
              flag: '🇮🇹'
            }, {
              code: 'en',
              label: 'English',
              flag: '🇬🇧'
            }].map(lang => <button key={lang.code} onClick={() => setLanguage(lang.code)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${language === lang.code ? 'border-green-600 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1503">
                    <div className="flex items-center gap-3" data-id="element-1504">
                      <span className="text-2xl" data-id="element-1505">{lang.flag}</span>
                      <span className="text-sm font-normal text-gray-900" data-id="element-1506">
                        {lang.label}
                      </span>
                    </div>
                    {language === lang.code && <Check className="h-5 w-5 text-green-600" data-id="element-1507" />}
                  </button>)}
              </div>
            </div>
          </div>
        </>}

      {/* ── Impact Detail Modal ── */}
      {showImpactDetailModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => closeModal('impact', setShowImpactDetailModal)} data-id="element-1508" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${animClass('impact')}`} data-id="element-1509">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1510">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1511" />
              <div className="flex items-center justify-between" data-id="element-1512">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1513">
                  Il tuo impatto ambientale
                </h3>
                <button onClick={() => closeModal('impact', setShowImpactDetailModal)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1514">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1515" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1516">
              <div className="space-y-8" data-id="element-1517">
                {/* Section 1: Impact Distribution */}
                <section data-id="element-1518">
                  <h4 className="text-sm font-medium text-gray-900 mb-4 uppercase tracking-wider" data-id="element-1519">
                    Distribuzione per categorie
                  </h4>
                  <div className="space-y-4" data-id="element-1520">
                    {[{
                  label: 'Trasporti',
                  value: 450,
                  emoji: '🚗'
                }, {
                  label: 'Energia',
                  value: 320,
                  emoji: '⚡'
                }, {
                  label: 'Rifiuti',
                  value: 180,
                  emoji: '♻️'
                }, {
                  label: 'Alimentazione',
                  value: 200,
                  emoji: '🍽️'
                }, {
                  label: 'Acqua',
                  value: 60,
                  emoji: '💧'
                }, {
                  label: 'Natura',
                  value: 40,
                  emoji: '🌳'
                }].map((item, index) => <div key={index} data-id="element-1521">
                        <div className="flex items-center justify-between mb-1.5" data-id="element-1522">
                          <div className="flex items-center gap-2" data-id="element-1523">
                            <span className="text-lg" data-id="element-1524">{item.emoji}</span>
                            <span className="text-sm text-gray-700" data-id="element-1525">
                              {item.label}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900" data-id="element-1526">
                            {item.value} kg
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-1527">
                          <div className="h-full rounded-full bg-green-600" style={{
                      width: `${item.value / 1250 * 100}%`
                    }} data-id="element-1528" />
                        </div>
                      </div>)}
                  </div>
                </section>

                {/* Section 2: Blockchain Tracking */}
                <section data-id="element-1529">
                  <div className="flex items-center gap-2 mb-4" data-id="element-1530">
                    <Shield className="h-5 w-5 text-green-600" data-id="element-1531" />
                    <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wider" data-id="element-1532">
                      Tracciamento Blockchain
                    </h4>
                  </div>
                  <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden" data-id="element-1533">
                    {[{
                  emoji: '🚲',
                  action: 'Andare in bici al lavoro',
                  date: '15 Gen 2024',
                  hash: '0x7a3f...9b2c'
                }, {
                  emoji: '🌳',
                  action: 'Piantare alberi',
                  date: '10 Gen 2024',
                  hash: '0x4b8e...7d1a'
                }, {
                  emoji: '🧹',
                  action: 'Pulizia spiaggia',
                  date: '5 Gen 2024',
                  hash: '0x2c9a...5f3b'
                }].map((item, index) => <div key={index} className="p-4 border-b border-gray-100 last:border-0 hover:bg-white transition-colors" data-id="element-1534">
                        <div className="flex items-start justify-between mb-2" data-id="element-1535">
                          <div className="flex items-center gap-3" data-id="element-1536">
                            <span className="text-xl" data-id="element-1537">{item.emoji}</span>
                            <div data-id="element-1538">
                              <p className="text-sm font-medium text-gray-900" data-id="element-1539">
                                {item.action}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1540">
                                {item.date}
                              </p>
                            </div>
                          </div>
                          <Shield className="h-4 w-4 text-green-600" data-id="element-1541" />
                        </div>
                        <div className="flex items-center gap-2 bg-white px-2 py-1.5 rounded border border-gray-200 w-fit mt-1" data-id="element-1542">
                          <Link className="h-3 w-3 text-gray-400" data-id="element-1543" />
                          <span className="text-[10px] font-mono text-gray-500" data-id="element-1544">
                            {item.hash}
                          </span>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-4 bg-blue-50 rounded-xl p-4 border border-blue-100" data-id="element-1545">
                    <p className="text-xs text-blue-800 leading-relaxed" data-id="element-1546">
                      <span className="font-medium" data-id="element-1547">
                        Certificazione Blockchain:
                      </span>{' '}
                      Le tue azioni certificate sono registrate in modo
                      immutabile sulla blockchain, garantendo trasparenza e
                      verificabilità del tuo impatto positivo.
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </>}
    </div>;
}